import React, { useState, useEffect } from 'react';
import { Table, Card, Form, Input, Select, Button, message, Space, Alert, Tag, Drawer, Radio, InputNumber } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { getOrderList, syncOrderDetail, createOrder } from '../../../api/order';
import { getCorpList } from '../../../api/corp';
import { useSuite } from '../../../context/SuiteContext';
import { copyToClipboard } from '../../../utils/clipboard';
import './OrderList.css';

const { Option } = Select;

const OrderList = () => {
    const { suiteId } = useSuite();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [corpList, setCorpList] = useState([]);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('wechat'); // 'wechat' or 'alipay'
    const [totalPrice, setTotalPrice] = useState(0);
    const [form] = Form.useForm();
    const [createForm] = Form.useForm();

    const fetchCorpList = async () => {
        try {
            const res = await getCorpList({ suite_id: suiteId });
            if (res.code === 0) {
                setCorpList(res.data.list);
            } else {
                message.error(res.message || '获取企业列表失败');
            }
        } catch (error) {
            console.error('Fetch corp list failed:', error);
        }
    };

    // 检查企业是否在试用期内（提前7天)
    const isInTrialPeriod = (licenseCheckTime) => {
        if (!licenseCheckTime || licenseCheckTime === 0) {
            return false;
        }
        const now = Math.floor(Date.now() / 1000);
        const sevenDaysInSeconds = 7 * 24 * 60 * 60;
        const operateThreshold = licenseCheckTime - sevenDaysInSeconds;
        return now <= operateThreshold;
    };

    const fetchOrders = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const values = await form.validateFields();
            const params = {
                page,
                page_size: pageSize,
                suite_id: suiteId,
                ...values,
            };

            // Remove empty values
            Object.keys(params).forEach(key => {
                if (params[key] === undefined || params[key] === '') {
                    delete params[key];
                }
            });

            const res = await getOrderList(params);
            if (res.code === 0) {
                setData(res.data.list);
                setPagination({
                    ...pagination,
                    current: res.data.page,
                    pageSize: res.data.page_size,
                    total: res.data.total,
                });
            } else {
                message.error(res.message || '获取订单列表失败');
            }
        } catch (error) {
            console.error('Fetch orders failed:', error);
        } finally {
            setLoading(false);
        }
    };

    // 计算总价格
    const calculateTotalPrice = () => {
        const values = createForm.getFieldsValue(['account_count', 'buy_months']);
        const accountCount = values.account_count;
        const buyMonths = values.buy_months;
        // 只有两个值都存在时才计算
        if (accountCount && buyMonths) {
            const unitPrice = 5; // 单价：5元
            const price = accountCount * buyMonths * unitPrice;
            setTotalPrice(price);
        } else {
            setTotalPrice(0);
        }
    };

    useEffect(() => {
        if (suiteId) {
            fetchOrders();
            fetchCorpList();
        }
    }, [suiteId]);

    const handleTableChange = (newPagination) => {
        fetchOrders(newPagination.current, newPagination.pageSize);
    };

    const onFinish = () => {
        fetchOrders(1, pagination.pageSize);
    };

    const onReset = () => {
        form.resetFields();
        fetchOrders(1, pagination.pageSize);
    };

    const handleCreateOrderClick = () => {
        setDrawerVisible(true);
        setPaymentMethod('alipay');
        setTotalPrice(0);
        // Set default value for account_type
        createForm.setFieldsValue({ account_type: 2 });
    };

    const handleCreateOrderClose = () => {
        setDrawerVisible(false);
        createForm.resetFields();
        setTotalPrice(0);
    };

    const handleCreateOrderSubmit = async () => {
        try {
            const values = await createForm.validateFields();
            setSubmitLoading(true);

            const params = {
                suite_id: suiteId, // Although not explicitly asked to send suite_id, usually it's needed. If strictly following user request, remove it. But user said "corp/get_list_all?suite_id=xxx", implying suite context. The API doc says: corpid, account_type, account_count, buy_months. I will stick to the requested params.
                corpid: values.corpid,
                account_type: values.account_type,
                account_count: values.account_count,
                buy_months: values.buy_months
            };

            const res = await createOrder(params);
            if (res.code === 0) {
                message.success('创建订单成功');
                handleCreateOrderClose();
                fetchOrders(1, pagination.pageSize);
            } else {
                message.error(res.message || '创建订单失败');
            }
        } catch (error) {
            console.error('Create order failed:', error);
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleSyncOrder = async (record) => {
        try {
            let res;
            res = await syncOrderDetail({ order_no: record.order_no });

            if (res.code === 0) {
                message.success('同步成功');
                fetchOrders(pagination.current, pagination.pageSize);
            } else {
                message.error(res.message || '同步失败');
            }
        } catch (error) {
            console.error('Sync order failed:', error);
            message.error('同步请求失败');
        }
    };

    const columns = [
        {
            title: '订单编号',
            dataIndex: 'order_no',
            key: 'order_no',
            width: 200,
            align: 'center',
            fixed: 'left',
        },
        {
            title: '企业信息',
            key: 'corp_info',
            align: 'center',
            width: 200,
            render: (_, record) => {
                const corpId = record.corp_id || '-';
                const displayId = corpId.length > 20 ? corpId.substring(0, 20) + '...' : corpId;
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.6' }}>
                        <div>{record.corp_name || '-'}</div>
                        <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px', color: '#888', justifyContent: 'center' }}>
                            <span title={corpId}>{displayId}</span>
                            <CopyOutlined
                                style={{ marginLeft: 8, cursor: 'pointer', color: '#1890ff' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(corpId);
                                }}
                            />
                        </div>
                    </div>
                );
            },
        },
        // {
        //     title: '应用信息',
        //     key: 'suite_info',
        //     align: 'center',
        //     render: (_, record) => (
        //         <div>
        //             <div>{record.agent_name || '-'}</div>
        //             {/* <div style={{ fontSize: '12px', color: '#888' }}>{record.suite_id}</div> */}
        //         </div>
        //     ),
        // },
        {
            title: '订单类型',
            dataIndex: 'order_type',
            key: 'order_type',
            width: 140,
            align: 'center',
            render: (type) => {
                const typeMap = {
                    1: '购买账号',
                    2: '续期账号',
                    5: '应用版本付费迁移订单',
                    6: '历史合同迁移订单'
                };
                return typeMap[type] || type;
            },
        },
        {
            title: '账号类型',
            dataIndex: 'account_type',
            key: 'account_type',
            width: 120,
            align: 'center',
            render: (type) => {
                const typeMap = {
                    1: '基础账号',
                    2: '互通账号'
                };
                return typeMap[type] || type;
            },
        },
        {
            title: '账号有效期',
            dataIndex: 'account_duration',
            key: 'account_duration',
            width: 100,
            align: 'center',
            render: (text) => `${text}个月`,
        },
        {
            title: '下单数量',
            dataIndex: 'account_count',
            key: 'account_count',
            width: 100,
            align: 'center',
        },
        {
            title: '已购得数量',
            dataIndex: 'account_count_total',
            key: 'account_count_total',
            width: 100,
            align: 'center',
        },
        {
            title: '待使用数量',
            dataIndex: 'unused_account_count',
            key: 'unused_account_count',
            width: 100,
            align: 'center',
            render: (text) => (
                <span style={{ color: text > 0 ? '#22c57c' : '#ea0d0dff' }}>
                    {text > 0 ? text : 0}
                </span>
            )
        },
        {
            title: '订单状态',
            dataIndex: 'order_status',
            key: 'order_status',
            width: 120,
            align: 'center',
            render: (status) => {
                const statusMap = {
                    0: { text: '待支付', color: 'default' },
                    1: { text: '已支付', color: 'success' },
                    2: { text: '已取消', color: 'default' },
                    3: { text: '未支付过期', color: 'error' },
                    4: { text: '申请退款中', color: 'warning' },
                    5: { text: '退款成功', color: 'default' },
                    6: { text: '退款被拒绝', color: 'error' },
                    7: { text: '订单已失效', color: 'default' },
                };
                const config = statusMap[status] || { text: status, color: 'default' };
                return <Tag color={config.color}>{config.text}</Tag>;
            },
        },
        {
            title: '订单金额',
            dataIndex: 'order_price',
            key: 'order_price',
            width: 100,
            align: 'center',
            render: (price) => `¥${(price / 100).toFixed(2)}`,
        },
        {
            title: '创建时间',
            dataIndex: 'create_at',
            key: 'create_at',
            width: 160,
            align: 'center',
        },
        {
            title: '支付时间',
            dataIndex: 'pay_time',
            key: 'pay_time',
            width: 160,
            align: 'center',
            render: (text) => text || '-',
        },
        {
            title: '操作',
            key: 'action',
            fixed: 'right',
            width: 120,
            align: 'center',
            render: (_, record) => (
                <span className="action-link" onClick={() => handleSyncOrder(record)}>
                    更新订单
                </span>
            ),
        },
    ];

    return (
        <div className="order-list-container">
            <div className="wecom-header">
                <span>订单管理</span>
                <Button className="orange-button" onClick={handleCreateOrderClick}>购买账号</Button>
            </div>
            <Alert
                message="若已付款，订单状态仍然展示未支付，则可点击《更新订单》按钮，主动刷新订单支付状态！"
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
            />
            <Card bordered={false} className="search-card">
                <Form
                    form={form}
                    layout="inline"
                    onFinish={onFinish}
                >
                    <Form.Item name="corp_id">
                        <Select
                            placeholder="请选择企业"
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            style={{ width: 200 }}
                        >
                            {corpList.map(corp => (
                                <Option key={corp.id} value={corp.corp_id}>
                                    {corp.corp_name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="order_status">
                        <Select placeholder="请选择订单状态" allowClear style={{ width: 150 }}>
                            <Option value={0}>待支付</Option>
                            <Option value={1}>已支付</Option>
                            <Option value={2}>已取消</Option>
                            <Option value={3}>未支付过期</Option>
                            <Option value={4}>申请退款中</Option>
                            <Option value={5}>退款成功</Option>
                            <Option value={6}>退款被拒绝</Option>
                            <Option value={7}>订单已失效</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" className='orange-button' htmlType="submit" loading={loading}>
                                查询
                            </Button>
                            <Button onClick={onReset}>重置</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>

            <Card bordered={false} style={{ marginTop: 16 }}>
                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="order_no"
                    pagination={{
                        ...pagination,
                        showTotal: (total) => `共 ${total} 条`,
                        showSizeChanger: true,
                        showQuickJumper: true,
                    }}
                    loading={loading}
                    onChange={handleTableChange}
                    scroll={{ x: 2400 }}
                />
            </Card>

            <Drawer
                title="购买账号"
                width={500}
                onClose={handleCreateOrderClose}
                open={drawerVisible}
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={handleCreateOrderClose}>取消</Button>
                            <Button type="primary" onClick={handleCreateOrderSubmit} loading={submitLoading} className="orange-button">
                                立即支付
                            </Button>
                        </Space>
                    </div>
                }
            >
                <Form
                    form={createForm}
                    layout="vertical"
                >
                    <Form.Item
                        name="corpid"
                        label="选择企业"
                        rules={[{ required: true, message: '请选择企业' }]}
                    >
                        <Select
                            placeholder="请选择企业"
                            showSearch
                            optionFilterProp="children"
                        >
                            {corpList.map(corp => {
                                const inTrial = isInTrialPeriod(corp.license_check_time);
                                return (
                                    <Option key={corp.id} value={corp.corp_id} disabled={inTrial}>
                                        <span>
                                            {corp.corp_name}
                                            {inTrial && <span style={{ color: 'red', marginLeft: 8 }}>试用期内无需购买</span>}
                                        </span>
                                    </Option>
                                );
                            })}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="account_type"
                        label="账号类型"
                        rules={[{ required: true, message: '请选择账号类型' }]}
                    >
                        <Radio.Group buttonStyle="block">
                            <Radio.Button value={2}>互通账号</Radio.Button>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        name="account_count"
                        label="购买数量"
                        rules={[
                            { required: true, message: '请输入购买数量' },
                            { type: 'integer', min: 1, message: '请输入正整数' }
                        ]}
                    >
                        <InputNumber 
                            style={{ width: '100%' }} 
                            placeholder="请输入购买数量" 
                            min={1} 
                            precision={0}
                            onChange={calculateTotalPrice}
                        />
                    </Form.Item>

                    <Form.Item
                        name="buy_months"
                        label="有效时长（月）"
                        extra="一个月按31天算"
                        rules={[
                            { required: true, message: '请输入有效时长' },
                            { type: 'integer', min: 1, max: 12, message: '请输入1-12之间的正整数' }
                        ]}
                    >
                        <InputNumber 
                            style={{ width: '100%' }} 
                            placeholder="请输入有效时长" 
                            min={1} 
                            max={12} 
                            precision={0}
                            onChange={calculateTotalPrice}
                        />
                    </Form.Item>

                    <Form.Item
                        label="选择支付"
                        rules={[{ required: true, message: '请选择支付方式' }]}
                    >
                        <Space>
                            <Button 
                                onClick={() => setPaymentMethod('alipay')}
                                style={{
                                    backgroundColor: paymentMethod === 'alipay' ? '#009FE8' : '#fff',
                                    color: paymentMethod === 'alipay' ? '#fff' : '#000',
                                    borderColor: paymentMethod === 'alipay' ? '#009FE8' : '#d9d9d9',
                                    padding: '6px 16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <svg viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" width="20" height="20"><path d="M1024.0512 701.0304V196.864A196.9664 196.9664 0 0 0 827.136 0H196.864A196.9664 196.9664 0 0 0 0 196.864v630.272A196.9152 196.9152 0 0 0 196.864 1024h630.272a197.12 197.12 0 0 0 193.8432-162.0992c-52.224-22.6304-278.528-120.32-396.4416-176.64-89.7024 108.6976-183.7056 173.9264-325.3248 173.9264s-236.1856-87.2448-224.8192-194.048c7.4752-70.0416 55.552-184.576 264.2944-164.9664 110.08 10.3424 160.4096 30.8736 250.1632 60.5184 23.1936-42.5984 42.496-89.4464 57.1392-139.264H248.064v-39.424h196.9152V311.1424H204.8V267.776h240.128V165.632s2.1504-15.9744 19.8144-15.9744h98.4576V267.776h256v43.4176h-256V381.952h208.8448a805.9904 805.9904 0 0 1-84.8384 212.6848c60.672 22.016 336.7936 106.3936 336.7936 106.3936zM283.5456 791.6032c-149.6576 0-173.312-94.464-165.376-133.9392 7.8336-39.3216 51.2-90.624 134.4-90.624 95.5904 0 181.248 24.4736 284.0576 74.5472-72.192 94.0032-160.9216 150.016-253.0816 150.016z" fill="currentColor"></path></svg>
                                支付宝
                            </Button>

                            <Button 
                                onClick={() => setPaymentMethod('wechat')}
                                style={{
                                    backgroundColor: paymentMethod === 'wechat' ? '#15BA11' : '#fff',
                                    color: paymentMethod === 'wechat' ? '#fff' : '#000',
                                    borderColor: paymentMethod === 'wechat' ? '#15BA11' : '#d9d9d9',
                                    padding: '6px 16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <svg viewBox="0 0 1228 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" width="20" height="20"><path d="M530.8928 703.1296a41.472 41.472 0 0 1-35.7376-19.8144l-2.7136-5.5808L278.272 394.752a18.7392 18.7392 0 0 1-2.048-8.1408 19.968 19.968 0 0 1 20.48-19.3536c4.608 0 8.8576 1.4336 12.288 3.84l234.3936 139.9296a64.4096 64.4096 0 0 0 54.528 5.9392L1116.2624 204.8C1004.9536 80.896 821.76 0 614.4 0 275.0464 0 0 216.576 0 483.6352c0 145.7152 82.7392 276.8896 212.2752 365.5168a38.1952 38.1952 0 0 1 17.2032 31.488 44.4928 44.4928 0 0 1-2.1504 12.3904l-27.6992 97.4848c-1.3312 4.608-3.328 9.3696-3.328 14.1312 0 10.752 9.216 19.3536 20.48 19.3536 4.4032 0 8.0384-1.536 11.776-3.584l134.5536-73.3184c10.1376-5.5296 20.7872-8.96 32.6144-8.96 6.2976 0 12.288 0.9216 18.0736 2.5088 62.72 17.0496 130.4576 26.5728 200.5504 26.5728C953.7024 967.168 1228.8 750.592 1228.8 483.6352c0-80.9472-25.4464-157.1328-70.0416-224.1024l-604.9792 436.992-4.4544 2.4064a42.1376 42.1376 0 0 1-18.432 4.1984z" fill="currentColor"></path></svg>
                                微信支付
                            </Button>

                        </Space>
                    </Form.Item>

                    <div style={{
                        padding: '16px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px',
                        textAlign: 'center',
                        marginBottom: '16px'
                    }}>
                        <div style={{ color: '#666', marginBottom: '8px' }}>应付金额</div>
                        <div style={{
                            fontSize: '32px',
                            fontWeight: 'bold',
                            color: '#ff7a45'
                        }}>
                            ¥{totalPrice.toFixed(2)}
                        </div>
                    </div>
                </Form>
            </Drawer>
        </div>
    );
};

export default OrderList;
